// Generate diagonal-stripe hatching patterns at runtime for MapLibre fill-pattern.
//
// Each pattern is a small ImageData drawn with translucent diagonal lines
// matching standard chart conventions:
//
//   TFR              red,    crosshatch (both diagonals)
//   Prohibited       red,    dense diagonals
//   Restricted       orange, diagonals
//   Warning          yellow, diagonals
//   Alert            amber,  diagonals (sparse)
//   MOA              purple, diagonals
//
// MapLibre map.addImage() accepts ImageData; we register one per type.

import type { Map as MlMap } from 'maplibre-gl';

interface PatternSpec {
  id: string;
  /** Base fill RGBA (background tint at low alpha). */
  bg: [number, number, number, number];
  /** Stripe RGBA. */
  stripe: [number, number, number, number];
  /** Pattern size (px). 16 is a good default. */
  size?: number;
  /** Stripe spacing (px). */
  spacing?: number;
  /** Stripe width (px). */
  thickness?: number;
  /** If true, draw lines in BOTH diagonals (crosshatch). */
  crosshatch?: boolean;
}

// IMPORTANT: for diagonal stripes to tile seamlessly across pattern repeats,
// `size` must be a multiple of `spacing`. (Cross-checked by Codex.)
const SPECS: PatternSpec[] = [
  // TFRs: red crosshatch (size=16, spacing=4 → 16/4=4 stripes per tile)
  {
    id: 'hatch-tfr',
    bg: [239, 68, 68, 60],     // red-500 @ ~24%
    stripe: [220, 38, 38, 220],// red-600
    size: 16, spacing: 4, thickness: 1.5, crosshatch: true,
  },
  // Prohibited: red dense diagonals
  {
    id: 'hatch-prohibited',
    bg: [220, 38, 38, 60],
    stripe: [185, 28, 28, 220],
    size: 16, spacing: 4, thickness: 1.5,
  },
  // Restricted: orange diagonals
  {
    id: 'hatch-restricted',
    bg: [234, 88, 12, 60],
    stripe: [194, 65, 12, 230],
    size: 16, spacing: 4, thickness: 1.5,
  },
  // Warning: yellow diagonals
  {
    id: 'hatch-warning',
    bg: [202, 138, 4, 50],
    stripe: [161, 98, 7, 200],
    size: 18, spacing: 6, thickness: 1.5,
  },
  // Alert: amber sparser
  {
    id: 'hatch-alert',
    bg: [161, 98, 7, 40],
    stripe: [120, 53, 15, 180],
    size: 16, spacing: 8, thickness: 1.5,
  },
  // MOA: purple diagonals (chart convention)
  {
    id: 'hatch-moa',
    bg: [124, 58, 237, 50],
    stripe: [109, 40, 217, 200],
    size: 16, spacing: 4, thickness: 1.5,
  },
  // Generic fallback
  {
    id: 'hatch-other',
    bg: [251, 191, 36, 50],
    stripe: [180, 138, 35, 200],
    size: 18, spacing: 6, thickness: 1.5,
  },
];

function buildPattern(spec: PatternSpec): ImageData {
  const size = spec.size ?? 16;
  const spacing = spec.spacing ?? 5;
  const thickness = spec.thickness ?? 1.5;

  // Use OffscreenCanvas if available, fall back to <canvas>. We type ctx loosely
  // because the 2d API surface used here (fillStyle/fillRect/strokeStyle/...) is
  // shared between CanvasRenderingContext2D and OffscreenCanvasRenderingContext2D.
  let ctx: any;
  if (typeof OffscreenCanvas !== 'undefined') {
    const c = new OffscreenCanvas(size, size);
    ctx = c.getContext('2d');
    if (!ctx) throw new Error('OffscreenCanvas 2d context unavailable');
  } else {
    const c = document.createElement('canvas');
    c.width = size; c.height = size;
    ctx = c.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
  }

  // Background fill
  ctx.fillStyle = `rgba(${spec.bg.join(',')})`;
  ctx.fillRect(0, 0, size, size);

  // Stripes — use clipping to the tile so seams are clean across tile borders.
  ctx.strokeStyle = `rgba(${spec.stripe.join(',')})`;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'butt';

  // Draw diagonal lines from upper-left to lower-right (45deg).
  // We draw lines at intervals, extending slightly outside to avoid gaps.
  const drawDiag = (slope: 1 | -1) => {
    for (let i = -size; i < size * 2; i += spacing) {
      ctx.beginPath();
      if (slope === 1) {
        ctx.moveTo(i, 0);
        ctx.lineTo(i + size, size);
      } else {
        ctx.moveTo(i, size);
        ctx.lineTo(i + size, 0);
      }
      ctx.stroke();
    }
  };

  drawDiag(1);
  if (spec.crosshatch) drawDiag(-1);

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Register all hatching patterns on a MapLibre map. Idempotent — safe to call
 * after a style reload.
 */
export function registerHatchPatterns(map: MlMap) {
  for (const spec of SPECS) {
    if (map.hasImage(spec.id)) continue;
    try {
      const img = buildPattern(spec);
      // MapLibre accepts ImageData via addImage with the right shape.
      map.addImage(spec.id, img as unknown as { width: number; height: number; data: Uint8ClampedArray });
    } catch (err) {
      // If something goes wrong, just skip — fill-pattern will fall back.
      // eslint-disable-next-line no-console
      console.warn('[fyrefly] hatch pattern failed:', spec.id, err);
    }
  }
}

/** Map a SUA TYPE_CODE letter to one of our pattern ids. */
export function suaPatternId(typeCode: string | null | undefined): string {
  switch ((typeCode || '').toUpperCase()) {
    case 'P': return 'hatch-prohibited';
    case 'R': return 'hatch-restricted';
    case 'W': return 'hatch-warning';
    case 'A': return 'hatch-alert';
    case 'M': return 'hatch-moa';
    default:  return 'hatch-other';
  }
}
